/** Small planar-geometry helpers used to seed the position solver. */

export type P2 = [number, number];

/**
 * Intersections of two circles (c0, r0) and (c1, r1). Returns 0, 1 or 2 points.
 * Used to produce a good initial guess for a free joint constrained by two bars.
 */
export function circleCircle(c0: P2, r0: number, c1: P2, r1: number): P2[] {
  const dx = c1[0] - c0[0];
  const dy = c1[1] - c0[1];
  const d = Math.hypot(dx, dy);
  if (d < 1e-12) return [];
  if (d > r0 + r1 + 1e-9) return [];
  if (d < Math.abs(r0 - r1) - 1e-9) return [];
  const a = (r0 * r0 - r1 * r1 + d * d) / (2 * d);
  const h2 = r0 * r0 - a * a;
  const h = Math.sqrt(Math.max(0, h2));
  const xm = c0[0] + (a * dx) / d;
  const ym = c0[1] + (a * dy) / d;
  if (h < 1e-9) return [[xm, ym]];
  const ox = (-h * dy) / d;
  const oy = (h * dx) / d;
  return [
    [xm + ox, ym + oy],
    [xm - ox, ym - oy],
  ];
}

/** Pick the circle-circle intersection closer to `hint` (falls back to midpoint). */
export function circleCirclePick(
  c0: P2,
  r0: number,
  c1: P2,
  r1: number,
  hint: P2,
): P2 {
  const pts = circleCircle(c0, r0, c1, r1);
  if (pts.length === 0) {
    return [(c0[0] + c1[0]) / 2, (c0[1] + c1[1]) / 2];
  }
  let best = pts[0];
  let bestD = Infinity;
  for (const p of pts) {
    const dd = Math.hypot(p[0] - hint[0], p[1] - hint[1]);
    if (dd < bestD) {
      bestD = dd;
      best = p;
    }
  }
  return best;
}

export const dist2 = (a: P2, b: P2): number => Math.hypot(a[0] - b[0], a[1] - b[1]);
